import { yupResolver } from '@hookform/resolvers/yup';
import { useSaleorAuthContext } from '@saleor/auth-sdk/react';
import type { LinkProps } from 'next/link';
import type { Dispatch, ReactNode, SetStateAction } from 'react';
import { useEffect, useRef, useState } from 'react';
import type ReCAPTCHA from 'react-google-recaptcha';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { Col } from '@/components/Col';
import { FormProvider } from '@/components/FormProvider';
import { Grid } from '@/components/Grid';
import { InputField } from '@/components/InputField';
import { SectionHeader } from '@/components/SectionHeader';
import { ServerErrors } from '@/components/ServerErrors';
import type { AccountError } from '@/graphql/saleor/api';
import { useCheckout } from '@/hooks/useCheckout';
import { useGoogleAnalytics } from '@/hooks/useGoogleAnalytics';
import { useRouter } from '@/hooks/useRouter';
import {
  endDeliveryLoginSignInInFlight,
  setDeliveryLoginSignInInFlight,
} from '@/lib/auth/deliveryLoginSignInGuard';
import { LOGIN_FAIL_MAX_COUNT, MOCK_DEFAULT_EMAIL } from '@/lib/const';
import { CONTENTSQUARE_MASKED } from '@/lib/forms/const';
import { genericErrorHandler } from '@/lib/forms/helpers';
import { EVENT_NAME } from '@/lib/gtm/const';
import { paths } from '@/lib/paths';
import { generateDataTestId } from '@/lib/tests';
import type { ApiErrorWithType, WithChildren } from '@/lib/types';
import { formMessages } from '@/messages/forms';
import { useRegionProvider } from '@/providers/RegionProvider';
import { checkoutActions } from '@/states/checkout';
import type { ButtonColorVariant } from '@/theme/types/components';

import type { InputColorVariant } from '../Input';
import LinkWithLocale from '../LinkWithLocale/LinkWithLocale';
import { ReCaptcha } from '../ReCaptcha';
import type { LoginSchema } from './schema';
import { loginSchema } from './schema';

import * as S from './AccountBox.styles';
import * as M from './messages';

export const AccountBox = ({ children }: WithChildren) => {
  return (
    <S.Container data-test-id='AccountBoxContainer'>{children}</S.Container>
  );
};

const GuestCheckoutBox = ({ link }: { link?: LinkProps }) => {
  const { formatMessage } = useIntl();
  const { sendEcommerceEventByLines, sendEvent } = useGoogleAnalytics();
  const {
    snapshot: { checkout },
  } = useCheckout<true>();

  const onHandleGuestCheckout = () => {
    sendEvent({
      event: EVENT_NAME.GUEST_CHECKOUT,
    });

    sendEcommerceEventByLines({
      coupon: checkout?.voucherCode ?? undefined,
      event: EVENT_NAME.BEGIN_CHECKOUT,
      lines: checkout?.lines,
      totalValue: Math.abs(checkout?.totalBalance.amount),
    });
  };

  return (
    <S.Section data-test-id='AccountBoxGuestCheckoutBox' variant='guest'>
      <SectionHeader
        color='white'
        data-test-id='AccountBoxGuestCheckoutSectionHeader'
        numberOfColoredWords={2}
        text={formatMessage(M.messages.checkoutAsGuest)}
        textAlign='center'
      />
      <S.Description data-test-id='AccountBoxGuestCheckoutDescription'>
        {M.DontHaveAccountDescription}
      </S.Description>
      <S.ButtonContainer>
        <S.SubmitButton
          colorVariant='white'
          data-test-id='AccountBoxGuestCheckoutButton'
          label={M.CheckoutAsGuest}
          linkProps={link}
          onClick={onHandleGuestCheckout}
          {...generateDataTestId('button', 'guestCheckout')}
        />
      </S.ButtonContainer>
    </S.Section>
  );
};

export type LoginFormLayout = 'account' | 'delivery';

export interface LoginFormProps {
  buttonColorVariant?: ButtonColorVariant;
  emailPlaceholder?: string;
  inputColorVariant?: InputColorVariant;
  layout?: LoginFormLayout;
  onLoginSuccess?: () => void;
  schemaFactory?: typeof loginSchema;
  secondaryAction?: ReactNode;
  setErrors: Dispatch<SetStateAction<ApiErrorWithType[]>>;
}
const LoginForm = ({
  buttonColorVariant = 'white',
  emailPlaceholder,
  inputColorVariant = 'primaryLight',
  layout = 'account',
  onLoginSuccess,
  schemaFactory = loginSchema,
  secondaryAction,
  setErrors,
}: LoginFormProps) => {
  const { formatMessage } = useIntl();
  const [isVerifyingRecaptcha, setIsVerifyingRecaptchaStatus] = useState(false);
  const [loginCount, setLoginCount] = useState(0);
  const displayReCaptcha = loginCount >= LOGIN_FAIL_MAX_COUNT;
  const reCaptchaRef = useRef<ReCAPTCHA>(null);
  const [processing, setProcessing] = useState(false);
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const schema = schemaFactory(formatMessage, displayReCaptcha);
  const { signIn } = useSaleorAuthContext();
  const { query, replace } = useRouter();
  const { sendEcommerceEventByLines, sendEvent } = useGoogleAnalytics();
  const {
    query: { languageCode },
  } = useRegionProvider();

  const {
    snapshot: { checkout },
  } = useCheckout<true>();

  const formCtx = useForm({
    defaultValues: schema.getDefault(),
    resolver: yupResolver(schema),
  });

  const captcha = formCtx.watch('captcha');

  const errorHandler = genericErrorHandler({
    formatMessage,
    formErrorsHandler: formCtx.setError,
    apiErrorsHandler: (errors, errorType) =>
      setErrors(
        errors.map((error) => ({
          type: errorType,
          ...error,
        })),
      ),
    errorType: 'accountSignIn',
  });

  const handleSubmit: SubmitHandler<LoginSchema> = async ({
    email,
    password,
  }) => {
    setProcessing(true);
    setShouldRedirect(false);
    const isDeliveryLogin = layout === 'delivery';

    if (isDeliveryLogin) {
      setErrors([]);
    }

    if (isDeliveryLogin) {
      setDeliveryLoginSignInInFlight(true);
    }

    try {
      const { data } = await signIn({
        email,
        password,
      });

      const errors = data.tokenCreate.errors as AccountError[];
      if (errors.length) {
        if (isDeliveryLogin) {
          formCtx.clearErrors();
          setErrors(
            errors.map((error) => ({
              type: 'accountSignIn',
              ...error,
            })),
          );
          sendEvent({
            event: EVENT_NAME.CHECKOUT_DELIVERY_LOGIN_FAILURE,
          });
        } else {
          errorHandler(errors);
          formCtx.setError('password', { message: '' }); // Saleor API tokenCreate mutation returns error only for the email field. This setError is needed to have also warning for password field.
        }
        if (displayReCaptcha) {
          formCtx.setValue('captcha', !displayReCaptcha);
          reCaptchaRef?.current?.reset();
        }
        setProcessing(false);
        setShouldRedirect(false);
        setLoginCount(loginCount + 1);
        return;
      }
    } finally {
      if (isDeliveryLogin) {
        endDeliveryLoginSignInInFlight();
      }
    }

    if (
      checkout?.email &&
      checkout?.email !== email &&
      checkout?.email !== MOCK_DEFAULT_EMAIL
    ) {
      await checkoutActions.clear();
    }

    setProcessing(false);
    setLoginCount(0);

    if (onLoginSuccess) {
      sendEvent({
        event: EVENT_NAME.LOGIN,
        account_authentification: 'regular',
        login_location: isDeliveryLogin ? 'checkout_delivery' : undefined,
        login_type: 'regular',
      });
      onLoginSuccess();
      return;
    }

    setShouldRedirect(true);
  };

  useEffect(() => {
    const handleProcessing = async () => {
      if (shouldRedirect === true) {
        sendEvent({
          event: EVENT_NAME.LOGIN,
          account_authentification: 'regular',
          login_type: 'regular',
        });

        if (query?.variant === 'checkout') {
          sendEcommerceEventByLines({
            coupon: checkout?.voucherCode ?? undefined,
            event: EVENT_NAME.BEGIN_CHECKOUT,
            lines: checkout?.lines,
            totalValue: Math.abs(checkout?.totalBalance.amount),
          });
        }
        await replace((query?.next as string) ?? paths.myAccount.url());
      }
    };

    handleProcessing();
  }, [shouldRedirect]);

  const resolvedEmailPlaceholder =
    emailPlaceholder ??
    (layout === 'delivery'
      ? formatMessage(formMessages.email)
      : formatMessage(formMessages.emailAddress));
  const passwordPlaceholder = formatMessage(formMessages.password);

  const isDeliveryLayout = layout === 'delivery';

  const emailField = (
    <InputField
      aria-label={resolvedEmailPlaceholder}
      autoComplete='email'
      borderColorVariant={isDeliveryLayout ? 'lightGray' : undefined}
      className={CONTENTSQUARE_MASKED}
      colorVariant={inputColorVariant}
      data-test-id='AccountBoxLoginFormEmailInput'
      disabled={processing}
      name='email'
      placeholder={resolvedEmailPlaceholder}
      type='email'
      {...(isDeliveryLayout ? {} : { required: true })}
    />
  );

  const passwordField = (
    <InputField
      aria-label={passwordPlaceholder}
      autoComplete='current-password'
      borderColorVariant={isDeliveryLayout ? 'lightGray' : undefined}
      colorVariant={inputColorVariant}
      data-test-id='AccountBoxLoginFormPasswordInput'
      disabled={processing}
      name='password'
      placeholder={passwordPlaceholder}
      type='password'
      {...(isDeliveryLayout ? {} : { required: true })}
    />
  );

  const submitLogin = () => {
    void formCtx.handleSubmit(handleSubmit)();
  };

  const submitButton = (
    <S.SubmitButton
      $layout={layout}
      colorVariant={buttonColorVariant}
      disabled={
        processing || (displayReCaptcha && !captcha) || isVerifyingRecaptcha
      }
      isProcessing={isVerifyingRecaptcha}
      label={M.LogIn}
      name='buttonSignin'
      onClick={isDeliveryLayout ? submitLogin : undefined}
      type={isDeliveryLayout ? 'button' : 'submit'}
      {...generateDataTestId('button', 'signIn')}
    />
  );

  return (
    <FormProvider
      name='formLoginMyAccount'
      {...formCtx}
      onSubmit={handleSubmit}
    >
      <S.FieldsWrapper
        $layout={layout}
        data-test-id='AccountBoxLoginFormFieldsWrapper'
      >
        {layout === 'delivery' ? (
          <>
            <Grid
              gridTemplateColumns='repeat(12, 1fr)'
              style={{ gridGap: 16, width: '100%' }}
            >
              <Col sm={6} xs={12}>
                <S.DeliveryFieldCell>{emailField}</S.DeliveryFieldCell>
              </Col>
              <Col sm={6} xs={12}>
                <S.DeliveryFieldCell>{passwordField}</S.DeliveryFieldCell>
              </Col>
            </Grid>
            {displayReCaptcha ? (
              <ReCaptcha
                ref={reCaptchaRef}
                data-test-id='AccountBoxLoginFormReCaptcha'
                languageCode={languageCode}
                name='captcha'
                onChange={setIsVerifyingRecaptchaStatus}
              />
            ) : null}
            <S.DeliveryActionsRow>
              {submitButton}
              {secondaryAction}
            </S.DeliveryActionsRow>
          </>
        ) : (
          <>
            {emailField}
            {passwordField}
            {displayReCaptcha ? (
              <ReCaptcha
                ref={reCaptchaRef}
                data-test-id='AccountBoxLoginFormReCaptcha'
                languageCode={languageCode}
                name='captcha'
                onChange={setIsVerifyingRecaptchaStatus}
              />
            ) : null}
            {submitButton}
          </>
        )}
      </S.FieldsWrapper>
    </FormProvider>
  );
};

const SignInBox = () => {
  const { formatMessage } = useIntl();
  const [errors, setErrors] = useState<ApiErrorWithType[]>([]);
  return (
    <S.Section data-test-id='AccountBoxSignInBox' variant='sign-in'>
      <ServerErrors errors={errors} />
      <SectionHeader
        colorized={false}
        data-test-id='AccountBoxSignInSectionHeader'
        htmlElementTag='h2'
        text={formatMessage(M.messages.haveAnAccount)}
        textAlign='center'
      />
      <AccountBox.LoginForm setErrors={setErrors}></AccountBox.LoginForm>
      <LinkWithLocale
        data-test-id='AccountBoxForgotPasswordLink'
        href={paths.myAccount.forgottenPassword.asPath()}
      >
        {M.ForgotPassword}
      </LinkWithLocale>
    </S.Section>
  );
};

const SignUpBenefits = () => {
  const { formatMessage } = useIntl();

  return (
    <S.SignUpBenefitsWrapper>
      <p>{formatMessage(M.messages.signUpBenefitsIntro)}</p>
      <ul>
        <li>{formatMessage(M.messages.signUpBenefitWarranty)}</li>
        <li>{formatMessage(M.messages.signUpBenefitDocumented)}</li>
        <li>{formatMessage(M.messages.signUpBenefitPassport)}</li>
        <li>{formatMessage(M.messages.signUpBenefitWishlist)}</li>
        <li>{formatMessage(M.messages.signUpBenefitHistory)}</li>
        <li>{formatMessage(M.messages.signUpBenefitDetails)}</li>
      </ul>
    </S.SignUpBenefitsWrapper>
  );
};

const SignUpBox = () => {
  const { formatMessage } = useIntl();

  return (
    <S.Section data-test-id='AccountBoxSignUpBox' variant='sign-up'>
      <SectionHeader
        color='white'
        data-test-id='AccountBoxSignUpSectionHeader'
        numberOfColoredWords={2}
        text={formatMessage(M.messages.dontHaveAnAccount)}
        textAlign='center'
      />
      <SignUpBenefits />
      <S.ButtonContainer>
        <S.SubmitButton
          colorVariant='white'
          data-test-id='AccountBoxSignUpButton'
          label={formatMessage(formMessages.createMyAccount)}
          linkProps={{ href: paths.myAccount.signIn.url() }}
          variant='outlined'
        />
      </S.ButtonContainer>
    </S.Section>
  );
};

AccountBox.displayName = 'AccountBox';

AccountBox.SignUp = SignUpBox;
AccountBox.SignIn = SignInBox;
AccountBox.GuestCheckout = GuestCheckoutBox;
AccountBox.LoginForm = LoginForm;
